import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Clock, Mic, Users, Presentation, Coffee, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { agendaItems } from '../data/mock';

const typeConfig = {
  Keynote: { icon: Mic, color: 'text-[var(--accent-primary)]', bg: 'bg-[var(--accent-bg)]' },
  Panel: { icon: Users, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  Networking: { icon: Coffee, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
  Demo: { icon: Presentation, color: 'text-purple-400', bg: 'bg-purple-400/10' },
  Workshop: { icon: MessageSquare, color: 'text-orange-400', bg: 'bg-orange-400/10' },
  Fireside: { icon: MessageSquare, color: 'text-pink-400', bg: 'bg-pink-400/10' }
};

const AgendaSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const visibleItems = isExpanded ? agendaItems : agendaItems.slice(0, 4);

  return (
    <section ref={ref} className="section-padding">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-14"
        >
          <span className="text-sm text-[var(--accent-primary)] uppercase tracking-widest font-medium">Event Schedule</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mt-4 mb-4 md:mb-6">
            A Day Packed with
            <span className="text-[var(--accent-primary)]"> Collaboration</span>
          </h2>
          <p className="text-sm md:text-base text-[var(--text-secondary)]">
            Keynotes, panels, demos, workshops, and dedicated networking blocks designed for maximum value.
          </p>
        </motion.div>
        
        {/* Timeline */}
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="sync">
            {visibleItems.map((item, index) => {
              const config = typeConfig[item.type] || typeConfig.Keynote;
              const Icon = config.icon;
              
              return (
                <motion.div
                  key={item.time + item.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.03 }}
                  className="relative pl-6 sm:pl-8 pb-4 sm:pb-6 last:pb-0"
                >
                  {/* Timeline line */}
                  {index < visibleItems.length - 1 && (
                    <div className="absolute left-[4px] sm:left-[5px] top-6 sm:top-8 w-0.5 h-[calc(100%-20px)] sm:h-[calc(100%-24px)] bg-[var(--border-primary)]" />
                  )}
                  
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-1 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[var(--accent-primary)]" />
                  
                  {/* Content */}
                  <div className="p-3 sm:p-4 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                      <div className="flex items-center gap-2 text-[var(--text-muted)]">
                        <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm font-medium">{item.time}</span>
                      </div>
                      <span className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${config.bg} ${config.color} w-fit`}>
                        <Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                        {item.type}
                      </span>
                    </div>
                    <h3 className="text-sm sm:text-base font-semibold mb-1">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-[var(--text-muted)] line-clamp-2">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
          
          {/* Expand/Collapse button */}
          {agendaItems.length > 4 && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full mt-4 py-3 flex items-center justify-center gap-2 text-sm font-medium text-[var(--accent-primary)] hover:text-[var(--accent-hover)] bg-[var(--bg-secondary)] border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] rounded-xl transition-colors"
            >
              {isExpanded ? (
                <>
                  <span>Show Less</span>
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  <span>View Full Schedule ({agendaItems.length - 4} more)</span>
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </motion.button>
          )}
        </div>
        
        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.4 }}
          className="text-center text-xs sm:text-sm text-[var(--text-muted)] mt-6 md:mt-8"
        >
          All times in UTC. Full agenda with speaker details available after registration.
        </motion.p>
      </div>
    </section>
  );
};

export default AgendaSection;
